import {
  APY_RANGE_SETTING_COOKIE_KEY,
  CHARTS_PERIOD_SETTINGS_COOKIE_KEY,
  INCLUDE_REWARDS_SETTING_COOKIE_KEY,
  TVL_RANGE_SETTING_COOKIE_KEY,
} from '@/constants/cookie'
import type { VaultApyRange } from '@/types/apy'
import type { ChartPeriod } from '@/types/periods'
import { setCookieValue } from '@/utils/cookie/cookie'
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useMixpanel } from './mixpanel'

export const VALID_APY_RANGES: VaultApyRange[] = ['1day', '7day', '30day'] as const

export const VALID_CHART_PERIODS: ChartPeriod[] = [7, 30, 90, 180, 365]

export interface Settings {
  apyRange: VaultApyRange
  includeRewards: boolean
  tvlRange: [number, number]
  chartsPeriod: ChartPeriod
}

export const DEFAULT_SETTINGS: Settings = {
  apyRange: '7day',
  includeRewards: true,
  tvlRange: [100_000, 50_000_000_000], // 100k - 50b
  chartsPeriod: 30,
}

interface SettingsContextType extends Settings {
  handleApyRange: (apyRange: Settings['apyRange']) => void
  handleIncludeRewards: (includeRewards: Settings['includeRewards']) => void
  handleTvlRange: (tvlRange: Settings['tvlRange']) => void
  handleChartsPeriod: (chartRange: Settings['chartsPeriod']) => void
}

const SettingsContext = createContext<SettingsContextType>({
  ...DEFAULT_SETTINGS,
  handleApyRange: () => {},
  handleIncludeRewards: () => {},
  handleTvlRange: () => {},
  handleChartsPeriod: () => {},
})

interface Props {
  initialSettings?: Settings
  children: ReactNode
}

export const SettingsProvider = ({ initialSettings = DEFAULT_SETTINGS, children }: Props) => {
  const { trackEvent } = useMixpanel()
  const [apyRange, setApyRange] = useState(initialSettings.apyRange)
  const [includeRewards, setIncludeRewards] = useState(initialSettings.includeRewards)
  const [tvlRange, setTvlRange] = useState(initialSettings.tvlRange)
  const [chartsPeriod, setChartsPeriod] = useState(initialSettings.chartsPeriod)

  const handleApyRange = useCallback(
    (apyRange: VaultApyRange) => {
      setCookieValue(APY_RANGE_SETTING_COOKIE_KEY, apyRange)
      setApyRange(apyRange)
      trackEvent('APY range settings toggle', { Range: apyRange })
    },
    [trackEvent]
  )

  const handleIncludeRewards = useCallback(
    (includeRewards: Settings['includeRewards']) => {
      setCookieValue(INCLUDE_REWARDS_SETTING_COOKIE_KEY, includeRewards)
      setIncludeRewards(includeRewards)
      trackEvent('APY rewards settings toggle', { Rewards: includeRewards })
    },
    [trackEvent]
  )

  const handleTvlRange = useCallback(
    (tvlRange: Settings['tvlRange']) => {
      setCookieValue(TVL_RANGE_SETTING_COOKIE_KEY, JSON.stringify(tvlRange))
      setTvlRange(tvlRange)
      trackEvent('TVL range settings changed', { TVL: JSON.stringify(tvlRange) })
    },
    [trackEvent]
  )

  const handleChartsPeriod = useCallback(
    (chartsPeriod: ChartPeriod) => {
      setCookieValue(CHARTS_PERIOD_SETTINGS_COOKIE_KEY, chartsPeriod)
      setChartsPeriod(chartsPeriod)
      trackEvent('Charts period settings toggle', { Period: chartsPeriod })
    },
    [trackEvent]
  )

  const contextValue = useMemo(
    () => ({
      apyRange,
      includeRewards,
      tvlRange,
      chartsPeriod,
      handleApyRange,
      handleIncludeRewards,
      handleTvlRange,
      handleChartsPeriod,
    }),
    [
      apyRange,
      includeRewards,
      tvlRange,
      chartsPeriod,
      handleApyRange,
      handleIncludeRewards,
      handleTvlRange,
      handleChartsPeriod,
    ]
  )

  return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>
}

export const useSettingsContext = () => {
  const context = useContext(SettingsContext)
  if (!context) {
    throw new Error('useSettingsContext must be used within a SettingsProvider')
  }
  return context
}
